import { sessionSignal } from "@/api/session";
import { pineDrawingLimits } from "@/lib/preview-stream/drawingLimits";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { BacktestResult, BacktestTrade } from "@/api/types";
import { useApi } from "../context/ApiContext";
import { alignStoredBacktestConfig } from "@/lib/strategy-leverage";
import { ensurePinnedStrategyConfig } from "@/lib/script-save";
import { normalizeConfigurableParams } from "@/lib/configurable-params";
import { buildConfigPayload } from "../components/DynamicForm";
import {
  PREVIEW_TIMEOUT_MS,
  buildPreviewPayload,
  buildPreviewStreamPayload,
  buildPreviewSyncUpdate,
  hasEndDate,
  readCachedPreview,
  writeCachedPreview,
  toDateTime,
} from "@/lib/backtest-run";
import { fillsFromTrades, tailStartDate } from "@/lib/chart-tail";
import { resolveRecalculate } from "@/lib/recalculate";
import { effectiveBarMagnifier } from "@/lib/bar-magnifier";
import {
  StreamAccumulator,
  StreamGoneError,
  resolvePreviewStreamUrl,
  streamEvents,
} from "@/lib/preview-stream";

type StreamStatus =
  | "idle"
  | "connecting"
  | "replaying"
  | "live"
  | "ended"
  | "stale"
  | "failed"
  | "error";

type BacktestLike = {
  id: string;
  status?: string;
  enabled?: boolean;
  result?: BacktestResult | null;
  start_date?: string;
  end_date?: string | null;
  market_provider?: string;
  market_config?: Record<string, unknown>;
  symbol?: string;
  timeframe?: string;
  strategy_provider?: string;
  strategy_config?: Record<string, unknown>;
  engine_provider?: string;
  engine_config?: Record<string, unknown>;
  recalculate?: number;
  bar_magnifier?: string | null;
  metadata?: Record<string, unknown> | null;
};

export type UseBacktestPreviewOptions = {
  /**
   * `lab` (default): auto-preview when status is running / ?rerun=1.
   * `live`: realtime while enabled; one-shot `backtest.preview` when disabled.
   */
  mode?: "lab" | "live";
  /** Skip `backtest.update` before preview (Live config is already saved). */
  skipSync?: boolean;
};

function hasValidStartDate(bt: BacktestLike): boolean {
  return Boolean(toDateTime(bt.start_date));
}

export function useBacktestPreview(
  backtest: BacktestLike | undefined,
  options: UseBacktestPreviewOptions = {}
) {
  const { mode = "lab", skipSync = false } = options;
  const api = useApi(["scripts"]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [previewResult, setPreviewResult] = useState<BacktestResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);

  const [streamAcc, setStreamAcc] = useState<StreamAccumulator | null>(null);
  const [streamVersion, setStreamVersion] = useState(0);
  const [streamStatus, setStreamStatus] = useState<StreamStatus>("idle");
  const [streamBarCount, setStreamBarCount] = useState(0);
  const [fetchingCandles, setFetchingCandles] = useState(false);
  /** The chart shows only the newest candles while the full history is still streaming. */
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyBarCount, setHistoryBarCount] = useState(0);

  const runIdRef = useRef(0);
  const previewAbortRef = useRef<AbortController | null>(null);
  const btRef = useRef(backtest);
  btRef.current = backtest;
  const optsRef = useRef({ mode, skipSync });
  optsRef.current = { mode, skipSync };

  const streamAbortRef = useRef<AbortController | null>(null);
  const streamRunDoneRef = useRef<Promise<void> | null>(null);
  const flushScheduledRef = useRef(false);
  const currentAccRef = useRef<StreamAccumulator | null>(null);
  const lastUiFlushRef = useRef(0);
  /** True until Chart tab successfully loads candles for the latest Run. */
  const chartNeedsLoadRef = useRef(true);
  /** Counts chart streams started, so a late cleanup can tell it is stale. */
  const streamStartsRef = useRef(0);
  const streamStatusRef = useRef<StreamStatus>("idle");
  streamStatusRef.current = streamStatus;
  const fetchingRef = useRef(false);
  fetchingRef.current = fetchingCandles;

  const tailAbortRef = useRef<AbortController | null>(null);
  /** The quick chart a run started, so the chart stream that follows reuses it. */
  const pendingTailRef = useRef<{ run: number; promise: Promise<StreamAccumulator | null> } | null>(null);
  /** Trades of a finished run, by run id, for a quick chart that arrives after them. */
  const tailTradesRef = useRef<Record<number, BacktestTrade[] | undefined>>({});
  /** Latest finished result, for the trade markers of the quick chart. */
  const resultRef = useRef<BacktestResult | null>(null);
  resultRef.current = previewResult ?? backtest?.result ?? null;

  const stopStream = useCallback(() => {
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    }
    if (tailAbortRef.current) {
      tailAbortRef.current.abort();
      tailAbortRef.current = null;
    }
    pendingTailRef.current = null;
    setHistoryLoading(false);
    setHistoryBarCount(0);
    currentAccRef.current = null;
    streamRunDoneRef.current = null;
  }, []);

  /** Wipe all chart state (e.g. new Run, preference off). */
  const discardChartStream = useCallback(() => {
    // Abort the HTTP stream immediately (cheap). Defer clearing the React
    // accumulator — wiping hundreds of thousands of LWC points on the same
    // tick as "Running…" blocks the paint when Run is clicked from Chart.
    stopStream();
    setFetchingCandles(false);
    chartNeedsLoadRef.current = true;
    setStreamStatus("idle");
    // The clear is deferred, so a newer stream may have started by the time it
    // runs (always, in a background tab). Never wipe that one's data.
    const startsAtDiscard = streamStartsRef.current;
    const clearHeavy = () => {
      if (streamStartsRef.current !== startsAtDiscard) return;
      setStreamAcc(null);
      setStreamVersion(0);
      setStreamBarCount(0);
    };
    // A timer, not requestAnimationFrame: frames don't fire in a background tab.
    window.setTimeout(clearHeavy, 30);
  }, [stopStream]);

  /**
   * Leave Chart tab: abort any in-flight decode (frees CPU) but keep a finished
   * snapshot so re-opening the tab is instant when nothing changed.
   */
  const suspendChart = useCallback(() => {
    const status = streamStatusRef.current;
    const midFetch = status === "connecting" || status === "replaying" || fetchingRef.current;
    stopStream();
    setFetchingCandles(false);
    if (midFetch) {
      setStreamAcc(null);
      setStreamStatus("idle");
      setStreamVersion(0);
      setStreamBarCount(0);
      chartNeedsLoadRef.current = true;
    } else if (status === "live") {
      setStreamStatus("ended");
    }
  }, [stopStream]);

  const scheduleStreamFlush = useCallback((force = false) => {
    const a = currentAccRef.current;
    if (!a) return;
    const now = performance.now();
    if (!force && a.phase === "historical" && now - lastUiFlushRef.current < 250) {
      return;
    }
    if (flushScheduledRef.current && !force) return;
    flushScheduledRef.current = true;
    const flush = () => {
      flushScheduledRef.current = false;
      const acc = currentAccRef.current;
      if (!acc) return;
      lastUiFlushRef.current = performance.now();
      setStreamVersion(acc.version);
      setStreamBarCount(acc.nBars);
    };
    if (force) {
      flush();
      return;
    }
    if (typeof document === "undefined" || document.visibilityState === "visible") {
      requestAnimationFrame(flush);
    } else {
      setTimeout(flush, 250);
    }
  }, []);

  const prepareSynced = useCallback(
    async (bt: BacktestLike) => {
      const aligned = alignStoredBacktestConfig(bt);
      const strategy_config = ensurePinnedStrategyConfig(aligned.strategy_config, {
        metadata: bt.metadata,
        scripts: api.scripts || [],
        strategyProvider: bt.strategy_provider,
      });
      const rawSpecs = await api.commands.getParams({
        category: "market_provider",
        name: bt.market_provider || "binance",
        config: {},
      });
      const marketSpecs = normalizeConfigurableParams(rawSpecs).specs;
      const market_config = buildConfigPayload(bt.market_config || {}, marketSpecs);
      // Lab runs follow what the script declares now; a live strategy keeps what it runs with.
      const recalculate = optsRef.current.skipSync
        ? bt.recalculate ?? 0
        : await resolveRecalculate(api.commands, bt.strategy_provider, strategy_config, bt.recalculate);
      const synced = { ...bt, ...aligned, strategy_config, market_config, recalculate };
      if (!optsRef.current.skipSync) {
        await api.commands.updateBacktest(buildPreviewSyncUpdate({ ...synced, scripts: api.scripts || [] })
        );
      }
      return synced;
    },
    [api.commands, api.scripts]
  );

  const runOneShotPreview = useCallback(
    async (bt: BacktestLike, myRun: number, syncedIn?: BacktestLike) => {
      const synced = syncedIn ?? (await prepareSynced(bt));
      if (myRun !== runIdRef.current) return synced;
      const result = await api.commands.previewBacktest(buildPreviewPayload(synced),
        PREVIEW_TIMEOUT_MS, previewAbortRef.current?.signal
      );
      if (myRun !== runIdRef.current) return synced;
      // A quick chart already on screen gets this run's trades now.
      tailTradesRef.current = { [myRun]: result?.trades };
      const shownTail = currentAccRef.current;
      if (shownTail?.candlesAndPlotsOnly) {
        shownTail.fills = fillsFromTrades(result?.trades, shownTail.bars.time, shownTail.nBars);
        shownTail.version++;
        setStreamVersion(shownTail.version);
      }
      setPreviewResult(result);
      writeCachedPreview(synced, result);
      return synced;
    },
    [api.commands, prepareSynced]
  );

  /**
   * Quick chart for long runs: a short stream with only the newest candles, on
   * screen in about a second. Resolves with its accumulator once shown, or null
   * when the run is short (or it failed) and the full stream should just load.
   * Close this short stream before opening the full history stream.
   */
  const showTail = useCallback(
    async (synced: BacktestLike, myRun: number, trades: BacktestTrade[] | undefined): Promise<StreamAccumulator | null> => {
      const tailStart = tailStartDate(toDateTime(synced.start_date), synced.timeframe);
      if (!tailStart) return null;
      const controller = new AbortController();
      tailAbortRef.current = controller;
      const signal = AbortSignal.any([controller.signal, sessionSignal()]);
      let shown: StreamAccumulator | null = null;
      try {
        const link = (await api.commands.createPreviewStream(buildPreviewStreamPayload({ ...synced, start_date: tailStart }),
          PREVIEW_TIMEOUT_MS, signal
        )) as { url: string };
        if (myRun !== runIdRef.current) return null;
        const tail = new StreamAccumulator();
        tail.candlesAndPlotsOnly = true;
        for await (const ev of streamEvents(resolvePreviewStreamUrl(link.url), signal)) {
          if (myRun !== runIdRef.current) break;
          tail.ingest(ev);
          if (ev.t !== "historicalDone") continue;
          // Its own trades start too late to be the backtest's; the finished run's
          // are used instead (set later when the run is still in flight).
          tail.fills = fillsFromTrades(trades ?? tailTradesRef.current[myRun], tail.bars.time, tail.nBars);
          // From here a late clear of the previous chart must not wipe this one.
          streamStartsRef.current += 1;
          currentAccRef.current = tail;
          setStreamAcc(tail);
          setStreamVersion(tail.version);
          setStreamBarCount(tail.nBars);
          setStreamStatus("live");
          setHistoryLoading(true);
          shown = tail;
          break;
        }
      } catch (err) {
        // The quick chart is optional: the full stream still loads.
        if ((err as Error).name !== "AbortError") console.warn("preview_stream tail", err);
      } finally {
        controller.abort();
        if (tailAbortRef.current === controller) tailAbortRef.current = null;
      }
      return myRun === runIdRef.current ? shown : null;
    },
    [api.commands]
  );

  /** Candle stream — only started when Chart tab asks via loadChart(). */
  const startChartStream = useCallback(
    async (bt: BacktestLike, myRun: number) => {
      const requestSessionSignal = sessionSignal();
      const pendingTail = pendingTailRef.current?.run === myRun ? pendingTailRef.current : null;
      if (!pendingTail) stopStream();
      if (myRun !== runIdRef.current) {
        setFetchingCandles(false);
        return;
      }

      streamStartsRef.current += 1;
      setFetchingCandles(true);
      if (!pendingTail) {
        setStreamAcc(null);
        setStreamVersion(0);
        setStreamBarCount(0);
        setStreamStatus("connecting");
      }

      let synced: BacktestLike;
      try {
        synced = await prepareSynced(bt);
      } catch (err) {
        if (myRun !== runIdRef.current) return;
        setStreamStatus("error");
        setFetchingCandles(false);
        console.warn("preview_stream prepare failed", err);
        return;
      }
      if (myRun !== runIdRef.current) {
        setFetchingCandles(false);
        return;
      }
      if (!hasValidStartDate(synced)) {
        setStreamStatus("error");
        setFetchingCandles(false);
        return;
      }

      // Long runs: the newest candles come first from a short stream (already
      // under way when a run started it), then the full history follows.
      const tail = pendingTail
        ? await pendingTail.promise
        : await showTail(synced, myRun, resultRef.current?.trades);
      if (myRun !== runIdRef.current) {
        setFetchingCandles(false);
        return;
      }
      const tailShown = tail !== null && currentAccRef.current === tail;

      const controller = new AbortController();
      streamAbortRef.current = controller;
      const signal = AbortSignal.any([controller.signal, requestSessionSignal]);
      let link: { url: string };
      try {
        link = (await api.commands.createPreviewStream(buildPreviewStreamPayload(synced),
          PREVIEW_TIMEOUT_MS, signal
        )) as { url: string };
      } catch (err) {
        if (myRun !== runIdRef.current) return;
        setStreamStatus("error");
        setFetchingCandles(false);
        console.warn("preview_stream failed", err);
        return;
      }
      if (myRun !== runIdRef.current) {
        setFetchingCandles(false);
        return;
      }

      const magnified = Boolean(effectiveBarMagnifier(synced.timeframe, synced.bar_magnifier));
      const accumulator = new StreamAccumulator();
      accumulator.drawingLimits = pineDrawingLimits(
        (synced.strategy_config as { inputs?: { source?: string } } | undefined)?.inputs?.source
      );
      const url = resolvePreviewStreamUrl(link.url);
      let sawDone = false;
      /** True once the full history is on the chart (at once when there is no quick chart). */
      let fullShown = false;
      const showFull = () => {
        if (fullShown) return;
        fullShown = true;
        currentAccRef.current = accumulator;
        setStreamAcc(accumulator);
        setHistoryLoading(false);
      };

      if (!tailShown) showFull();

      let lastHistoryFlush = 0;
      streamRunDoneRef.current = (async () => {
        try {
          for await (const ev of streamEvents(url, signal)) {
            if (myRun !== runIdRef.current) break;
            accumulator.ingest(ev);
            if (ev.t === "bar") {
              if (fullShown || currentAccRef.current === null) {
                setStreamStatus((s) => (s === "connecting" ? "replaying" : s));
              }
              if (fullShown) scheduleStreamFlush(false);
              const now = performance.now();
              if (now - lastHistoryFlush > 250) {
                lastHistoryFlush = now;
                setHistoryBarCount(accumulator.nBars);
                // No quick chart yet: the skeleton shows the full stream's progress.
                if (!fullShown && currentAccRef.current === null) setStreamBarCount(accumulator.nBars);
              }
            } else if (ev.t === "historicalDone") {
              // The stream ran without the Bar Magnifier: mark the trades of the magnified run instead of its own.
              const trades = magnified ? resultRef.current?.trades ?? tailTradesRef.current[myRun] : undefined;
              if (trades?.length) {
                accumulator.fills = fillsFromTrades(trades, accumulator.bars.time, accumulator.nBars);
                accumulator.version++;
              }
              showFull();
              setStreamStatus("live");
              setFetchingCandles(false);
              chartNeedsLoadRef.current = false;
              scheduleStreamFlush(true);
            } else if (ev.t === "done") {
              sawDone = true;
            } else if (fullShown && accumulator.phase === "live") {
              scheduleStreamFlush(false);
            }
          }
          if (myRun !== runIdRef.current) return;
          showFull();
          if (!sawDone) {
            accumulator.markStale();
            setStreamStatus("stale");
          } else {
            setStreamStatus(accumulator.phase === "failed" ? "failed" : "ended");
            if (accumulator.phase !== "failed") chartNeedsLoadRef.current = false;
          }
          scheduleStreamFlush(true);
        } catch (err) {
          if (myRun !== runIdRef.current) return;
          if (err instanceof StreamGoneError) {
            setStreamStatus("error");
          } else if ((err as Error).name !== "AbortError") {
            setStreamStatus("error");
            console.warn("preview_stream", err);
          }
        } finally {
          setFetchingCandles(false);
          setHistoryLoading(false);
          flushScheduledRef.current = false;
          if (currentAccRef.current === accumulator) {
            setStreamVersion(accumulator.version);
            setStreamBarCount(accumulator.nBars);
            currentAccRef.current = null;
          }
          if (streamAbortRef.current === controller) {
            streamAbortRef.current = null;
          }
        }
      })();
    },
    [api.commands, prepareSynced, scheduleStreamFlush, showTail, stopStream]
  );

  /**
   * Called when the Chart tab becomes active. No-op if candles for the latest
   * Run are already loaded (or a fetch is in progress).
   */
  const loadChart = useCallback(() => {
    const bt = btRef.current;
    if (!bt?.id || !hasValidStartDate(bt)) return;
    // Need at least one Overview preview before spending candle bandwidth.
    if (!previewResult && !bt.result) return;
    if (fetchingRef.current) return;
    if (!chartNeedsLoadRef.current) return;
    void startChartStream(bt, runIdRef.current);
  }, [previewResult, startChartStream]);

  const runPreview = useCallback(
    async (bt: BacktestLike) => {
      const myRun = ++runIdRef.current;
      previewAbortRef.current?.abort();
      previewAbortRef.current = new AbortController();
      setPreviewing(true);
      setError(null);
      setStartedAt(Date.now());
      // Drop any chart work — candles load only when Chart tab is opened.
      discardChartStream();

      try {
        if (!hasValidStartDate(bt)) {
          throw new Error('invalid start_date "", expected YYYY-MM-DD or YYYY-MM-DD HH:MM:SS');
        }
        const synced = await prepareSynced(bt);
        if (myRun !== runIdRef.current) {
          setPreviewing(false);
          setStartedAt(null);
          return;
        }

        // The chart's newest candles load next to the run, not after it.
        if (optsRef.current.mode === "lab") {
          pendingTailRef.current = { run: myRun, promise: showTail(synced, myRun, undefined) };
        }

        await runOneShotPreview(bt, myRun, synced);
        if (myRun !== runIdRef.current) {
          setPreviewing(false);
          setStartedAt(null);
          return;
        }
        setPreviewing(false);
        setStartedAt(null);
        // Chart stays idle until loadChart() — keeps Overview/dates responsive.
      } catch (e: unknown) {
        if (myRun !== runIdRef.current) return;
        const msg = e instanceof Error ? e.message : "Preview failed";
        if (msg === "cancelled") return;
        setError(
          msg === "request timeout"
            ? "Backtest timed out after 10 minutes. Try a shorter date range or a higher timeframe."
            : msg
        );
        setPreviewing(false);
        setStartedAt(null);
      }
    },
    [discardChartStream, prepareSynced, runOneShotPreview, showTail]
  );

  /**
   * Abort the RPC and chart streams, then ignore any stale results.
   */
  const cancel = useCallback(() => {
    runIdRef.current += 1;
    previewAbortRef.current?.abort();
    stopStream();
    setFetchingCandles(false);
    setPreviewing(false);
    setStartedAt(null);
  }, [stopStream]);

  const run = useCallback(
    async (override?: Partial<BacktestLike> | null) => {
      const bt = { ...btRef.current, ...(override || {}) } as BacktestLike;
      if (!bt?.id) return;
      await runPreview(bt);
    },
    [runPreview]
  );

  useEffect(() => {
    const bt = btRef.current;
    if (!bt?.id) return;
    const rerun = searchParams.get("rerun") === "1";
    if (rerun) setSearchParams({}, { replace: true });

    if (optsRef.current.mode === "live") {
      if (bt.enabled) {
        const cached = readCachedPreview(bt);
        if (cached) setPreviewResult(cached);
      } else if (rerun) {
        void runPreview(bt);
      } else {
        const cached = readCachedPreview(bt);
        if (cached) setPreviewResult(cached);
        else if (hasValidStartDate(bt)) void runPreview(bt);
      }
    } else if (rerun || bt.status === "running") {
      if (hasValidStartDate(bt)) void runPreview(bt);
    } else {
      const cached = readCachedPreview(bt);
      if (cached) setPreviewResult(cached);
    }

    return () => {
      runIdRef.current += 1;
      previewAbortRef.current?.abort();
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backtest?.id, backtest?.market_provider, backtest?.enabled, mode]);

  // Reloading or leaving the site: cut the chart stream so nothing keeps decoding.
  useEffect(() => {
    window.addEventListener("pagehide", stopStream);
    return () => window.removeEventListener("pagehide", stopStream);
  }, [stopStream]);

  const displayResult = previewResult ?? backtest?.result ?? null;

  return {
    previewing,
    previewResult,
    displayResult,
    error,
    setError,
    startedAt,
    run,
    finite: Boolean(backtest && hasEndDate(backtest)),
    streamAcc,
    streamVersion,
    streamStatus,
    streamBarCount,
    fetchingCandles,
    historyLoading,
    historyBarCount,
    chartLoading: fetchingCandles || streamStatus === "connecting" || streamStatus === "replaying",
    loadChart,
    cancel,
    suspendChart,
    discardChartStream,
  };
}
